import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { AMENITIES, PROPERTY_TYPES } from "@/lib/fees";
import { EarningsCalculator } from "@/components/EarningsCalculator";
import { HostBenefits } from "@/components/HostBenefits";
import { LEGAL } from "@/pages/Legal";
import PhotoUploader from "@/components/PhotoUploader";
import { useJurisdictions, Requirements } from "@/components/Jurisdictions";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export const BLANK = {
  title: "", description: "", location: "", property_type: "House", price_per_night: 120, cleaning_fee: 40,
  max_guests: 2, bedrooms: 1, beds: 1, baths: 1, amenities: ["Wifi", "Kitchen"],
  house_rules: "Check-in after 3pm, check-out by 11am.\nNo smoking indoors.", photos: [], active: true,
  permit_number: "", permit_document: "", host_attestation: false,
  insurance_provider: "", insurance_expiry: "", insurance_document: "",
  state: "", county: "", city: "", str_license_number: "", room_tax_permit: "", license_expiry: "",
  room_tax_permit_expiry: "", jurisdiction: "", street_address: "", zip_code: "", cancellation_policy: "moderate", country: "United States", show_full_address: false,
};

const norm = (v) => (v || "").trim().toLowerCase().replace(/\./g, "");
export const inClark = (f) => ["nv", "nevada"].includes(norm(f.state)) && norm(f.county).replace(/ county$/, "") === "clark";

// Location decides which lodging taxes apply; license numbers show on the listing page.
export function TaxLicenseSection({ form }) {
  const { f, set, patch } = form;
  const { data: list = [] } = useJurisdictions();
  const pick = (key) => patch({ jurisdiction: key, ...(key ? { state: "Nevada", county: "Clark" } : {}) });
  const input = (k, label, ph, testid, type = "text") => (
    <Field label={label}><Input type={type} data-testid={testid} value={f[k] || ""} onChange={set(k)} placeholder={ph} /></Field>
  );
  const needed = !!f.jurisdiction || inClark(f);
  return (
    <div className="space-y-4 rounded-2xl border p-5" data-testid="listing-tax-license-section">
      <div>
        <p className="font-semibold">Location for taxes &amp; licenses</p>
        <p className="text-sm text-stone-600">We find the room tax zone from the home's physical street address using Clark County's official map. Your license and room tax permit numbers appear on your listing.</p>
      </div>
      <p className="text-xs text-stone-500">The tax zone comes from the property address above, checked against Clark County's official room tax map.</p>
      <ZoneStatus z={f.tax_zone} list={list} />
      <Field label="Your tax zone, if you know it (optional; the official map lookup decides)">
        <select className="w-full rounded-xl border bg-white px-3 py-2 text-sm" value={f.jurisdiction || ""} onChange={(e) => pick(e.target.value)} data-testid="listing-jurisdiction-select">
          <option value="">Not sure / outside Clark County</option>
          {list.map((j) => <option key={j.key} value={j.key}>{j.name}</option>)}
        </select>
      </Field>
      <Requirements j={list.find((j) => j.key === (f.tax_zone?.zone || f.jurisdiction))} testid="listing-jurisdiction-requirements" />
      <div className="grid gap-4 sm:grid-cols-2">
        {input("str_license_number", `Short-term rental license #${needed ? " (required)" : ""}`, "e.g. STR-123456", "listing-str-license-input")}
        {input("license_expiry", "License expires", "", "listing-license-expiry-input", "date")}
        {input("room_tax_permit", `Room tax permit / registration #${needed ? " (required)" : ""}`, "e.g. TRT-0001", "listing-room-tax-permit-input")}
        {input("room_tax_permit_expiry", "Room tax permit expires", "", "listing-room-tax-permit-expiry-input", "date")}
      </div>
      <p className="text-xs text-stone-500">We email you 30 days, 7 days and on the day your license or room tax permit expires.</p>
    </div>
  );
}

// Result of the official address lookup (shown after saving).
function ZoneStatus({ z, list }) {
  if (!z) return null;
  const ok = z.status === "verified";
  return (
    <div className={`rounded-2xl border p-4 text-sm ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid="listing-tax-zone-status">
      <p className="font-semibold">{ok ? "Tax zone verified" : "Tax zone needs review"}: {list.find((j) => j.key === z.zone)?.name || "not found yet"}</p>
      {z.matched_address && <p className="text-xs">Official map match: {z.matched_address}</p>}
      {!ok && <ul className="mt-1 list-disc pl-5 text-xs">{(z.flags || []).map((x) => <li key={x}>{x}</li>)}</ul>}
      {!ok && <p className="mt-1 text-xs">Guests can't book until the FairStay team confirms the zone.</p>}
    </div>
  );
}

export const Field = ({ label, children }) => <div className="space-y-2"><Label>{label}</Label>{children}</div>;

const filled = (v) => !!(v || "").trim();
const positive = (v) => v !== "" && Number(v) > 0;
const future = (v) => !!v && v > new Date().toISOString().slice(0, 10);

export function canSubmit(f) {
  const address = ["street_address", "city", "state", "zip_code", "country"].every((k) => filled(f[k])) && /^\d{5}(-\d{4})?$/.test(f.zip_code.trim());
  const basics = filled(f.title) && address && f.description.length >= 10;
  const numbers = positive(f.price_per_night) && positive(f.max_guests) && f.cleaning_fee !== "";
  const permit = f.host_attestation && (filled(f.permit_number) || !!f.permit_document);
  const insurance = !!f.insurance_document && filled(f.insurance_provider) && !!f.insurance_expiry;
  const local = !(f.jurisdiction || inClark(f)) || (filled(f.str_license_number) && filled(f.room_tax_permit)
    && future(f.license_expiry) && future(f.room_tax_permit_expiry));
  return basics && numbers && permit && insurance && local;
}

export function useListingForm(id) {
  const [f, setF] = useState(id ? null : BLANK);
  useEffect(() => {
    if (!id) return undefined;
    let live = true;
    api.get(`/listings/${id}`).then(({ data }) => live && setF((cur) => cur || { ...data, jurisdiction: data.declared_zone ?? data.jurisdiction ?? "" })).catch((e) => toast.error(errMsg(e)));
    return () => { live = false; };
  }, [id]);
  const patch = (obj) => setF((cur) => ({ ...cur, ...obj }));
  const set = (k) => (e) => patch({ [k]: e?.target ? e.target.value : e });
  const num = (k) => (e) => patch({ [k]: e.target.value === "" ? "" : Number(e.target.value) });
  const single = (k) => (v) => patch({ [k]: v[v.length - 1] || "" });
  const body = () => Object.fromEntries(Object.keys(BLANK).map((k) => [k, f[k] ?? BLANK[k]]));
  return { f, patch, set, num, single, body };
}

const NUMBER_FIELDS = [
  ["Max guests", "max_guests", "1", "1"], ["Bedrooms", "bedrooms", "0", "1"],
  ["Beds", "beds", "0", "1"], ["Baths", "baths", "0", "0.5"],
];

export function BasicsSection({ form }) {
  const { f, set, num } = form;
  return (
    <>
      <Field label="Title"><Input data-testid="listing-title-input" value={f.title} onChange={set("title")} placeholder="Sunny loft near the river" /></Field>
      <Field label="Description"><Textarea data-testid="listing-description-input" rows={5} value={f.description} onChange={set("description")} placeholder="What makes your place special?" /></Field>
      <div className="grid grid-cols-2 gap-6 sm:grid-cols-5">
        <Field label="Type">
          <Select value={f.property_type} onValueChange={set("property_type")}>
            <SelectTrigger data-testid="listing-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        {NUMBER_FIELDS.map(([label, k, min, step]) => (
          <Field key={k} label={label}><Input data-testid={`listing-${k === "max_guests" ? "guests" : k}-input`} type="number" min={min} step={step} value={f[k]} onChange={num(k)} /></Field>
        ))}
      </div>
    </>
  );
}

export function PricingSection({ form }) {
  const { f, num } = form;
  const { data: list = [] } = useJurisdictions();
  const zoneKey = f.tax_zone?.zone || f.jurisdiction;
  const zone = list.find((j) => j.key === zoneKey);
  const verified = f.tax_zone?.status === "verified" && f.tax_zone?.zone === zone?.key;
  const tax = zone
    ? { rate: zone.rate, label: `${zone.name}${verified ? " · verified" : " · pending verification"}` }
    : { rate: 0, label: inClark(f) ? "Add the home's physical address to see your local lodging tax" : "Any local lodging tax is added at checkout based on your location" };
  return (
    <>
      <HostBenefits />
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Price per night (USD)"><Input data-testid="listing-price-input" type="number" min="1" value={f.price_per_night} onChange={num("price_per_night")} /></Field>
        <Field label="Cleaning fee (USD, one-time)"><Input data-testid="listing-cleaning-input" type="number" min="0" value={f.cleaning_fee} onChange={num("cleaning_fee")} /></Field>
      </div>
      <EarningsCalculator price={Number(f.price_per_night) || 0} cleaning={Number(f.cleaning_fee) || 0} tax={tax} />
    </>
  );
}

export function AmenitiesSection({ form }) {
  const { f, patch } = form;
  const toggle = (a) => patch({ amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] });
  return (
    <Field label="Amenities">
      <div className="flex flex-wrap gap-2" data-testid="listing-amenities-picker">
        {AMENITIES.map((a) => {
          const on = f.amenities.includes(a);
          return (
            <button type="button" key={a} onClick={() => toggle(a)} data-testid={`amenity-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "border-primary bg-primary text-white" : "bg-white hover:border-stone-400"}`}>{a}</button>
          );
        })}
      </div>
    </Field>
  );
}

const asList = (v) => (v ? [v] : []);

export function PermitSection({ form }) {
  const { f, set, single, patch } = form;
  return (
    <div className="space-y-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="listing-permit-section">
      <div>
        <p className="font-semibold">Short-term rental approval</p>
        <p className="text-sm text-stone-600">Your listing goes live after our team checks that this home is allowed to be rented short-term. Changing these details sends it for review again.</p>
      </div>
      <Field label="Permit or registration number"><Input data-testid="listing-permit-number-input" value={f.permit_number || ""} onChange={set("permit_number")} placeholder="e.g. STR-2026-001234" /></Field>
      <Field label="Photo of the permit (private, only our team sees it)">
        <PhotoUploader value={asList(f.permit_document)} onChange={single("permit_document")} kind="permit" max={1} testid="listing-permit-doc" />
      </Field>
      <label className="flex items-start gap-3 text-sm text-stone-700">
        <Checkbox data-testid="listing-attestation-checkbox" checked={!!f.host_attestation} onCheckedChange={(v) => patch({ host_attestation: !!v })} className="mt-0.5" />
        I confirm I own this home or have written permission from the owner, landlord or HOA, and that renting it short-term is allowed under local rules.
      </label>
    </div>
  );
}

export function InsuranceSection({ form }) {
  const { f, set, single } = form;
  return (
    <div className="space-y-4 rounded-2xl border border-sky-200 bg-sky-50/60 p-5" data-testid="listing-insurance-section">
      <div>
        <p className="font-semibold">Short-term rental insurance (required)</p>
        <p className="text-sm text-stone-600">A regular homeowner's or renter's policy usually doesn't cover paying guests. You need a short-term rental policy with at least {LEGAL.minLiability} liability (see the <Link to="/host-agreement" target="_blank" className="font-semibold text-primary hover:underline">Host Agreement</Link>). If it expires, your listing is hidden automatically.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Insurance company"><Input data-testid="listing-insurance-provider-input" value={f.insurance_provider || ""} onChange={set("insurance_provider")} placeholder="e.g. Proper Insurance" /></Field>
        <Field label="Policy expiry date"><Input data-testid="listing-insurance-expiry-input" type="date" value={f.insurance_expiry || ""} onChange={set("insurance_expiry")} /></Field>
      </div>
      <Field label="Proof of insurance (photo of the policy page, private)">
        <PhotoUploader value={asList(f.insurance_document)} onChange={single("insurance_document")} kind="insurance" max={1} testid="listing-insurance-doc" />
      </Field>
    </div>
  );
}
